import React, { useState, useEffect } from "react";
import { T, SPARKY_SERVER_URL, EXAM_SUBJECTS, profileKey } from '../shared/constants';

// ── Activity stats helper (localStorage-based) ─────────────
function getActivityStats(profileId) {
  const key = profileKey("lumio_activity", profileId);
  const history = JSON.parse(localStorage.getItem(key) || "[]");
  const today = new Date();
  let streak = 0;
  const dates = [...new Set(history.map(h => h.date))].sort().reverse();
  for (let i = 0; i < dates.length; i++) {
    const d = new Date(dates[i]);
    const expected = new Date(today - i * 86400000).toDateString();
    if (d.toDateString() === expected) streak++;
    else break;
  }
  const weekAgo = Date.now() - 7 * 86400000;
  const weekHistory = history.filter(h => h.ts > weekAgo);
  const subjectStats = {};
  history.filter(h => h.ts > Date.now() - 30*86400000).forEach(h => {
    if (!subjectStats[h.subject]) subjectStats[h.subject] = { correct: 0, total: 0 };
    subjectStats[h.subject].correct += h.correct;
    subjectStats[h.subject].total += h.total;
  });
  let weakestSubject = null, lowestPct = 100;
  Object.entries(subjectStats).forEach(([subj, stats]) => {
    if (stats.total >= 5) {
      const pct = (stats.correct / stats.total) * 100;
      if (pct < lowestPct) { lowestPct = pct; weakestSubject = subj; }
    }
  });
  const lastSession = history.length > 0 ? history[history.length - 1] : null;
  const lastDate = lastSession ? new Date(lastSession.date) : null;
  const daysSince = lastDate ? Math.floor((today - lastDate) / 86400000) : null;
  return { daysSince, streak, weekSessions: weekHistory.length, weekCorrect: weekHistory.reduce((a,b)=>a+b.correct,0), weekTotal: weekHistory.reduce((a,b)=>a+b.total,0), subjectStats, weakestSubject, lowestPct, lastSession, history };
}

async function generateLumiInsight(stats, serverUrl) {
  const { daysSince, streak, weakestSubject, lowestPct } = stats;
  if (daysSince === null) return { type:"info", title:"Welcome to Empirio! \uD83D\uDC4B", message:"Start your first practice session to get personalised insights.", action:"Start Practising \u2192" };
  if (daysSince >= 7) return { type:"urgent", title:"It's been a while!", message:`No practice for ${daysSince} days. Just 10 minutes today makes a difference.`, action:"Resume Practice \u2192" };
  if (daysSince >= 3) return { type:"warning", title:"Keep the momentum going", message:`Last session was ${daysSince} days ago. Ready for a quick session?`, action:"Practice Now \u2192" };
  if (streak >= 7) return { type:"success", title:`\uD83D\uDD25 ${streak}-day streak!`, message:`Incredible consistency! ${streak} days in a row.`, action:null };
  if (weakestSubject && lowestPct < 50) {
    const programSubjects = EXAM_SUBJECTS[stats.examProgram] || [];
    if (!stats.examProgram || programSubjects.includes(weakestSubject)) {
      return { type:"info", title:"Lumi spotted a focus area", message:`${weakestSubject} is scoring ${Math.round(lowestPct)}% recently. A few targeted sessions here could make a big difference.`, action:`Practice ${weakestSubject} \u2192` };
    }
  }
  return null;
}

export default function LumiInsightsTab({ progress, serverUrl, activeProfile, examProgram }) {
  const [insight, setInsight] = useState(null);
  const stats = getActivityStats(activeProfile?.id);
  const { daysSince, streak, weekSessions, weekCorrect, weekTotal, subjectStats } = stats;
  const weekPct = weekTotal > 0 ? Math.round((weekCorrect / weekTotal) * 100) : 0;
  useEffect(() => {
    generateLumiInsight({ ...stats, examProgram }, serverUrl)
      .then(a => setInsight(a))
      .catch(() => setInsight("Keep practising \u2014 Lumi will have insights for you soon! \uD83C\uDF1F"));
  }, []);
  return (
    <div style={{ animation:"fadeUp .3s ease" }}>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:16 }}>
        {[
          { label:"Day Streak", value:streak||0, icon:"\uD83D\uDD25", color:T.gold },
          { label:"This Week", value:`${weekSessions} sessions`, icon:"\uD83D\uDCC5", color:T.accent },
          { label:"Week Accuracy", value:weekTotal>0?`${weekPct}%`:"\u2014", icon:"\uD83C\uDFAF", color:T.green },
          { label:"Last Practice", value:daysSince===null?"Never":daysSince===0?"Today":`${daysSince}d ago`, icon:"\u23F0", color:daysSince>=3?"#EF4444":T.green },
        ].map(stat => (
          <div key={stat.label} style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:12, padding:"14px" }}>
            <div style={{ fontSize:20, marginBottom:6 }}>{stat.icon}</div>
            <div style={{ fontSize:18, fontWeight:800, color:stat.color, marginBottom:2 }}>{stat.value}</div>
            <div style={{ fontSize:11, color:T.textMid, fontWeight:600 }}>{stat.label}</div>
          </div>
        ))}
      </div>
      {insight && (
        <div style={{ background:`${T.accent}10`, border:`1.5px solid ${T.accent}33`, borderRadius:12, padding:14, marginBottom:14 }}>
          <div style={{ display:"flex", gap:10, alignItems:"flex-start" }}>
            <span style={{ fontSize:24, flexShrink:0 }}>{"\u2728"}</span>
            <div>
              <p style={{ fontSize:13, fontWeight:800, color:T.accent, margin:"0 0 4px" }}>{insight.title}</p>
              <p style={{ fontSize:12, color:T.textMid, lineHeight:1.5, margin:0 }}>{insight.message}</p>
            </div>
          </div>
        </div>
      )}
      {Object.keys(subjectStats).length === 0 && (
        <div style={{ textAlign:"center", padding:"32px 16px", color:T.textMid }}>
          <div style={{ fontSize:40, marginBottom:12 }}>{"\u2728"}</div>
          <p style={{ fontSize:14, fontWeight:700, color:T.text, marginBottom:6 }}>No activity yet</p>
          <p style={{ fontSize:13 }}>Complete some practice sessions and Lumi will generate personalised insights here.</p>
        </div>
      )}
    </div>
  );
}
